import React, { createContext, useContext, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import axios from "../api/axios";
import { getCurrentFleetId, getCurrentUser } from "../utils/getCurrentFleet";

/**
 * Company-scoped permissions for the fleet app.
 *
 * TWO ORTHOGONAL GATES, DELIBERATELY SEPARATE
 * -------------------------------------------
 *   SubscriptionContext.hasFeature()  -> "is this capability SOLD to this
 *                                        company?" Driven by the plan.
 *   PermissionContext.can()           -> "is this USER allowed to use it?"
 *                                        Driven by their role in the company.
 *
 * Conflating them is a classic mistake: a company on the Optimization plan has
 * bought tax optimisation, but that does not mean every collaborator should be
 * able to change the subscription or delete a vehicle. Conversely a fleet
 * manager holds `fleet.billing.manage` regardless of plan, but the billing
 * screen still has nothing to offer if the plan does not include the feature.
 *
 * Both are answered by the server. The permission list comes from
 * GET /me/permissions?company_id=..., which resolves the roles assigned in the
 * `user_role` table - so promoting a collaborator to manager takes effect on
 * their next page load, with no release.
 *
 * As always, this is UX truthfulness, not security. The API Gates enforce.
 */

/** Keys the fleet app uses. Mirrors the `fleet.*` domain of the registry. */
export type FleetPermission =
  | "fleet.company.view"
  | "fleet.company.manage"
  | "fleet.user.view"
  | "fleet.user.manage"
  | "fleet.vehicle.view"
  | "fleet.vehicle.manage"
  | "fleet.billing.view"
  | "fleet.billing.manage";

interface MePermissionsResponse {
  user_id: number | null;
  is_super_admin: boolean;
  is_active: boolean;
  company_id: number | null;
  roles: { slug: string; name: string; scope: string; company_id: number | null }[];
  permissions: string[];
}

interface PermissionContextValue {
  permissions: string[];
  roles: MePermissionsResponse["roles"];
  /** The server has answered. Before that, `permissions` is a local guess. */
  isResolved: boolean;
  isLoading: boolean;
  can: (permission: FleetPermission | string) => boolean;
  canAny: (permissions: (FleetPermission | string)[]) => boolean;
  refresh: () => void;
}

const PermissionContext = createContext<PermissionContextValue>({
  permissions: [],
  roles: [],
  isResolved: false,
  isLoading: true,
  can: () => false,
  canAny: () => false,
  refresh: () => undefined,
});

export const permissionQueryKeys = {
  me: (companyId: number) => ["me", "permissions", companyId] as const,
};

/**
 * Best guess while the request is in flight, derived from the legacy
 * `company_role` string already in localStorage.
 *
 * Without this the whole UI would flicker read-only on every reload for the
 * fleet managers who make up most sessions. It is intentionally a superset of
 * nothing: an EMPLOYEE gets view-only, matching the old behaviour, and the
 * server's answer replaces it a moment later.
 */
const optimisticPermissions = (companyId: number): string[] => {
  const user = getCurrentUser() as any;
  if (!user) return [];

  const membership = (user.company_users ?? []).find(
    (cu: any) => Number(cu?.company_id) === companyId
  );
  if (!membership || Number(membership.is_active) !== 1) return [];

  if (membership.company_role === "FLEET_MANAGER") {
    return [
      "fleet.company.view",
      "fleet.user.view",
      "fleet.user.manage",
      "fleet.vehicle.view",
      "fleet.vehicle.manage",
      "fleet.billing.view",
      "fleet.billing.manage",
    ];
  }

  return ["fleet.company.view", "fleet.vehicle.view"];
};

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const companyId = getCurrentFleetId();
  const queryClient = useQueryClient();

  const { data, isSuccess, isPending } = useQuery({
    queryKey: permissionQueryKeys.me(companyId),
    queryFn: async (): Promise<MePermissionsResponse> => {
      const response = await axios.get("/me/permissions", {
        // Company-scoped roles only count for the company being asked about;
        // omitting this would silently return platform permissions only.
        params: { company_id: companyId || undefined },
      });
      return response.data?.data ?? response.data;
    },
    enabled: !!localStorage.getItem("token"),
    staleTime: 60_000,
    retry: 1,
  });

  const value = useMemo<PermissionContextValue>(() => {
    const resolved = isSuccess && !!data;
    const permissions = resolved
      ? data!.permissions ?? []
      : optimisticPermissions(companyId);

    const can = (permission: FleetPermission | string) =>
      permissions.includes(permission);

    return {
      permissions,
      roles: data?.roles ?? [],
      isResolved: resolved,
      isLoading: isPending,
      can,
      canAny: (list) => list.some(can),
      refresh: () =>
        queryClient.invalidateQueries({
          queryKey: permissionQueryKeys.me(companyId),
        }),
    };
  }, [data, isSuccess, isPending, companyId, queryClient]);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionContext);

/** Convenience for the common single-key case. */
export const useCan = (permission: FleetPermission | string): boolean =>
  usePermissions().can(permission);
